import React, { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const SortableTable = ({ 
  columns, 
  data, 
  renderRow, 
  loading = false, 
  emptyState = null,
  className = ""
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});

  // Helper function
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((value, key) => value?.[key], obj);
  };

  // Convert size strings to bytes for proper sorting
  const parseSize = (sizeStr) => {
    if (!sizeStr || sizeStr === 'N/A' || sizeStr === '-') return 0;
    
    const units = {
      'B': 1,
      'KB': 1024,
      'MB': 1024 * 1024,
      'GB': 1024 * 1024 * 1024,
      'TB': 1024 * 1024 * 1024 * 1024
    };
    
    const match = sizeStr.toString().match(/^([\d.,]+)\s*(B|KB|MB|GB|TB)$/i);
    if (!match) return 0;
    
    const value = parseFloat(match[1].replace(',', '.'));
    const unit = match[2].toUpperCase();
    
    return value * (units[unit] || 1);
  };

  // Handle sorting
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (aValue === bValue) return 0;

      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      // Handle size columns (check if the key contains 'size' or 'tamanho')
      const isSizeColumn = sortConfig.key.toLowerCase().includes('size') || 
                          sortConfig.key.toLowerCase().includes('tamanho') ||
                          sortConfig.key === 'Size';
      
      if (isSizeColumn) {
        const aSizeBytes = parseSize(aValue);
        const bSizeBytes = parseSize(bValue);
        return (aSizeBytes > bSizeBytes ? 1 : -1) * direction;
      }
      
      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue > bValue ? 1 : -1) * direction;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.toLowerCase().localeCompare(bValue.toLowerCase()) * direction;
      }

      return (aValue > bValue ? 1 : -1) * direction;
    });
  }, [data, sortConfig]);

  // Handle filtering
  const filteredData = useMemo(() => {
    if (!Object.keys(filters).length) return sortedData;

    return sortedData.filter(item => {
      return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;
        
        const itemValue = getNestedValue(item, key);
        const searchValue = filterValue.toLowerCase();
        
        if (typeof itemValue === 'string') {
          return itemValue.toLowerCase().includes(searchValue);
        }
        
        return String(itemValue).toLowerCase().includes(searchValue);
      });
    });
  }, [sortedData, filters]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUpIcon className="h-3 w-3 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUpIcon className="h-3 w-3 text-gray-600" />
      : <ChevronDownIcon className="h-3 w-3 text-gray-600" />;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-12 bg-gray-200 rounded"></div>
        ))}
      </div>
    );
  }

  if (!filteredData.length && emptyState) {
    return emptyState;
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      {/* Filter Inputs */}
      {columns.some(col => col.filterable) && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {columns.filter(col => col.filterable).map(column => (
            <div key={column.key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filtrar {column.label}:
              </label>
              <input
                type="text"
                placeholder={`Buscar por ${column.label.toLowerCase()}...`}
                value={filters[column.key] || ''}
                onChange={(e) => handleFilter(column.key, e.target.value)}
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                }`}
                onClick={column.sortable ? () => handleSort(column.key) : undefined}
              >
                <div className="flex items-center space-x-1">
                  <span>{column.label}</span>
                  {column.sortable && getSortIcon(column.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredData.map((item, index) => renderRow(item, index))}
        </tbody>
      </table>

      {/* Results Summary */}
      {Object.keys(filters).length > 0 && (
        <div className="mt-3 text-sm text-gray-600">
          Mostrando {filteredData.length} de {data.length} resultado(s)
          {Object.entries(filters).some(([_, value]) => value) && (
            <span className="ml-2">
              - Filtros ativos: {Object.entries(filters).filter(([_, value]) => value).length}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SortableTable;